import FeedbackSettingsForm from 'components/CreateAssignment/FeedbackSettingsForm';
import FeedbackTypeCard from 'components/CreateAssignment/FeedbackTypeCard';
import QuizFeedbackSettingsSection from 'components/CreateQuiz/QuizFeedbackSettingsSection';
import { useApp } from 'contexts/app';
import { PageContext } from 'pages/CreateQuiz/PageProvider';
import { useContext, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, styled, Text } from 'shell/components';
import { getQuizFeedbackDefaults, isQuizFeedbackSettingsDefault } from 'utils/quizFeedback';

const QuizFeedbackSettings = ({ device }) => {
    const { t } = useTranslation(['quiz', 'assignment', 'assignmentSettings']);
    const { isFeedbackFeatureDisabled, isGradingFeatureDisabled } = useApp();
    const { form, setForm, isEditable = true, feedbackSettingsErrors = {} } = useContext(PageContext);
    const id = useId();
    const feedbackEnabled = form.aiFeedbackEnabled && !isFeedbackFeatureDisabled;
    const settings = {
        ...form.feedbackSettings,
        feedbackCustomContext: form.feedbackCustomContext || '',
    };

    const handleChange = (changes) => {
        setForm((current) => {
            const currentSettings = {
                ...current.feedbackSettings,
                feedbackCustomContext: current.feedbackCustomContext || '',
            };
            const next = typeof changes === 'function' ? changes(currentSettings) : { ...currentSettings, ...changes };
            const { feedbackCustomContext, ...feedbackSettings } = next;
            return { ...current, feedbackSettings, feedbackCustomContext };
        });
    };

    const handleReset = () => {
        const defaults = getQuizFeedbackDefaults();
        handleChange({
            ...defaults.feedbackSettings,
            feedbackCustomContext: defaults.feedbackCustomContext,
        });
    };

    return (
        <Stack spacing={5}>
            <QuizFeedbackSettingsSection device={device} />
            {feedbackEnabled && (
                <>
                    <Stack spacing={2} sx={{ minWidth: 0 }}>
                        <Text component={'h2'} variant={'p1'} weight={'medium'}>
                            {t('assignment:feedback-section.feedback-structure-label', 'Feedback Structure')}
                        </Text>
                        <FeedbackTypeGrid>
                            {['rubric', 'system', 'templated', 'custom'].map((type) => (
                                <FeedbackTypeCard
                                    key={type}
                                    type={type}
                                    appearance={'quiz-edit'}
                                    isSelected={settings.structure === type}
                                    disabled={
                                        !isEditable ||
                                        (type === 'templated' && (!form.aiGradingEnabled || isGradingFeatureDisabled))
                                    }
                                    onClick={() => handleChange({ structure: type })}
                                />
                            ))}
                        </FeedbackTypeGrid>
                        {(!form.aiGradingEnabled || isGradingFeatureDisabled) && (
                            <Text color={'light'}>
                                {t(
                                    'assignmentSettings:panels.feedback.templatesRequireGrading',
                                    'Enable grading suggestions to use Smart Feedback Templates.'
                                )}
                            </Text>
                        )}
                    </Stack>
                    <FeedbackForm>
                        <FeedbackSettingsForm
                            disabled={!isEditable}
                            data={form}
                            form={settings}
                            errors={feedbackSettingsErrors}
                            handleChange={handleChange}
                            idPrefix={`${id}-`}
                            isResetDisabled={isQuizFeedbackSettingsDefault(
                                form.feedbackSettings,
                                form.feedbackCustomContext
                            )}
                            onReset={handleReset}
                        />
                    </FeedbackForm>
                </>
            )}
        </Stack>
    );
};

const FeedbackTypeGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: theme.spacing(5),
    minWidth: 0,
    [theme.breakpoints.down('tablet')]: {
        display: 'flex',
        paddingBottom: theme.spacing(3),
        overflowX: 'auto',
        '& > *': {
            flex: '0 0 220px',
        },
    },
}));

const FeedbackForm = styled(Box)(({ theme }) => ({
    minWidth: 0,
    '& > .MuiStack-root': {
        gap: theme.spacing(2),
    },
    '& > .MuiStack-root > .MuiPaper-root': {
        padding: theme.spacing(5),
    },
    '& .MuiFormControl-root:has(> .MuiInputBase-root)': {
        width: '241px',
        maxWidth: '100%',
    },
    '& .MuiFormControl-root > .MuiInputBase-root': {
        width: '100% !important',
    },
    '& .MuiStack-root:has(> .MuiRadioGroup-root)': {
        gap: theme.spacing(4),
    },
    '& .MuiRadioGroup-root > .MuiStack-root': {
        width: '105px',
    },
}));

export default QuizFeedbackSettings;
